import type { UploadApi } from "@wayflyer/flyui";
import { useUploadManager } from "@wayflyer/flyui";
import type { FundingApplicationSchemas } from "api-types";
import { useAxios } from "auth-sdk";
import { useMemo } from "react";

import {
  BANK_STATEMENTS_CONFIRM_UPLOAD_API,
  BANK_STATEMENTS_CREATE_UPLOAD_API,
  BANK_STATEMENTS_DELETE_UPLOAD_API,
} from "../../../components/constants";

export interface BankStatementUpload {
  name: string;
  objectKey: string;
}

const getUploadedFileId = (uploadedFile: BankStatementUpload) => uploadedFile.objectKey;

export const useBankAccountsUploadManager = (customerDomain: string) => {
  const client = useAxios();

  const createUploadUrl = BANK_STATEMENTS_CREATE_UPLOAD_API.replace(":customerSlug", customerDomain);
  const confirmUploadUrl = BANK_STATEMENTS_CONFIRM_UPLOAD_API.replace(":customerSlug", customerDomain);
  const deleteUploadUrl = BANK_STATEMENTS_DELETE_UPLOAD_API.replace(":customerSlug", customerDomain);

  const api = useMemo<UploadApi<BankStatementUpload>>(
    () => ({
      createPresignedUrl: async (file: File) => {
        const { data } = await client.post<FundingApplicationSchemas.UploadUrlSchema>(createUploadUrl, {
          name: file.name,
          size: file.size,
          type: file.type,
        });
        return { id: data.objectKey, urlOptions: data.urlOptions };
      },
      confirmUpload: async (id: string): Promise<BankStatementUpload> => {
        const { data } = await client.post<BankStatementUpload>(confirmUploadUrl, { objectKey: id });
        return data;
      },
      deleteUpload: async (uploadedFile: BankStatementUpload): Promise<void> => {
        await client.post(deleteUploadUrl, { key: uploadedFile.objectKey });
      },
    }),
    [client, createUploadUrl, confirmUploadUrl, deleteUploadUrl]
  );

  return useUploadManager<BankStatementUpload>(api, undefined, { getUploadedFileId });
};
